class TheGrid extends HTMLElement {
  constructor() {
    super();
    this.notification = null;
    this.tabButtons = null;
    this.productRows = null;
    this.viewMoreRows = null;
    this._priceObserver = null;
    this._orderBaselines = new WeakMap();
    this.isWholesaleUser = this.dataset.isWholesaleUser === 'true';
    this.initializeElements();
    this.attachEventListeners();
    this.setupPriceObservers();
    this.syncAllProductRowPriceLabelsAndTotals();
    this.querySelectorAll('[data-submit-product-order]').forEach((btn) => {
      btn.disabled = true;
    });
    void this.initializeCartQuantities();
  }

  disconnectedCallback() {
    if (this._priceObserver) {
      this._priceObserver.disconnect();
      this._priceObserver = null;
    }
  }

  initializeElements() {
    this.notification = document.createElement('div');
    this.notification.className = 'notification';
    this.appendChild(this.notification);

    this.tabButtons = this.querySelectorAll('.tab-button');
    this.productRows = this.querySelectorAll('.product-row');
    this.viewMoreRows = this.querySelectorAll('.view-more-row');
  }

  /**
   * Parses a money-like display string (e.g. WPD-updated handler text) to integer cents.
   */
  parseMoneyTextToCents(text) {
    if (!text || typeof text !== 'string') return NaN;
    const n = parseFloat(text.trim().replace(/[^0-9.-]+/g, ''));
    if (Number.isNaN(n)) return NaN;
    return Math.round(n * 100);
  }

  escapeHtmlForPriceRange(str) {
    if (str == null || str === '') return '';
    const el = document.createElement('div');
    el.textContent = String(str);
    return el.innerHTML;
  }

  getProductRowDetailsRoot(productRow) {
    return productRow?.querySelector('.product-row__cell--details') ?? null;
  }

  getProductRowCommerceRoot(productRow) {
    return productRow?.querySelector('.product-row__cell--commerce') ?? null;
  }

  updateColorHeadingFromDom(productRow) {
    const tpl = productRow.dataset.colorHeadingTemplate;
    const detailsRoot = this.getProductRowDetailsRoot(productRow);
    const scope = detailsRoot ?? productRow;
    const heading = scope.querySelector('[data-color-heading]');
    if (!tpl || !heading) {
      return;
    }
    const activeSwatch = scope.querySelector(
      '[data-the-grid-swatch].button--swatch-active'
    );
    if (activeSwatch) {
      const val =
        activeSwatch.getAttribute('title') ||
        activeSwatch.querySelector('.visually-hidden')?.textContent?.trim() ||
        '';
      heading.textContent = tpl.split('__COLOR__').join(val);
      return;
    }

    const checked = scope.querySelector(
      '.product-row__colors-group input[type="radio"]:checked'
    );
    if (!checked) {
      return;
    }
    const val = checked.value || '';
    heading.textContent = tpl.split('__COLOR__').join(val);
  }

  refreshProductRowCompareDisplay(productRow) {
    const commerceRoot = this.getProductRowCommerceRoot(productRow);
    const compareEl = commerceRoot?.querySelector('[data-compare-display]');
    const rrpEl = commerceRoot?.querySelector('[data-rrp-line]');
    if (!compareEl) {
      return;
    }

    const handler = productRow.querySelector('[data-wpd-price-handler]');
    if (!handler) {
      compareEl.textContent = '';
      compareEl.hidden = true;
      if (rrpEl) {
        rrpEl.hidden = true;
      }
      return;
    }

    const priceCents = parseInt(handler.getAttribute('data-wpd-variant-price'), 10);
    const compareAttr = handler.getAttribute('data-wpd-variant-compare-at-price');
    const compareCents = compareAttr ? parseInt(compareAttr, 10) : NaN;

    if (
      Number.isNaN(priceCents) ||
      Number.isNaN(compareCents) ||
      compareCents <= priceCents
    ) {
      compareEl.textContent = '';
      compareEl.hidden = true;
      if (rrpEl) {
        rrpEl.hidden = true;
      }
      return;
    }

    compareEl.hidden = false;
    compareEl.textContent = this.formatMoney(compareCents);
    const pct = Math.round(((compareCents - priceCents) / compareCents) * 100);
    if (rrpEl) {
      const tpl = this.dataset.rrpPercentTemplate;
      if (tpl) {
        rrpEl.textContent = tpl.split('__PCT__').join(String(pct));
      } else {
        rrpEl.textContent = `RRP · ${pct}% off`;
      }
      rrpEl.hidden = false;
    }
  }

  invokeReplaceWPDPriceHandlers() {
    if (typeof replaceWPDPrice === 'function') {
      replaceWPDPrice(document.querySelectorAll('[data-wpd-price-handler]'));
    }
  }

  syncAllProductRowPriceLabelsAndTotals() {
    this.querySelectorAll('.product-row').forEach((row) => {
      this.refreshProductRowPriceLabelFromHandlers(row);
      this.refreshProductRowCompareDisplay(row);
      this.refreshRowTotal(row);
    });
  }

  /**
   * Sets [data-price] from handler unit prices: one value if all match, else "From … to …".
   */
  refreshProductRowPriceLabelFromHandlers(productRow) {
    const commerceRoot = this.getProductRowCommerceRoot(productRow);
    const priceEl = commerceRoot?.querySelector('[data-price]');
    if (!priceEl) return;

    const handlers = productRow.querySelectorAll('[data-wpd-price-handler]');
    const centsList = [];
    handlers.forEach((h) => {
      const c = this.parseMoneyTextToCents(h.textContent);
      if (!Number.isNaN(c)) {
        centsList.push(c);
      }
    });

    if (centsList.length === 0) {
      return;
    }

    const min = Math.min(...centsList);
    const max = Math.max(...centsList);

    if (min === max) {
      priceEl.classList.remove('product-row__price-current--range');
      priceEl.textContent = this.formatMoney(min);
      return;
    }

    const lowStr = this.formatMoney(min);
    const highStr = this.formatMoney(max);
    const tpl = this.dataset.priceRangeTemplate;

    priceEl.classList.add('product-row__price-current--range');

    if (tpl && tpl.includes('__LOW__') && tpl.includes('__HIGH__')) {
      const [beforeLow, midAndAfter] = tpl.split('__LOW__');
      const midParts = (midAndAfter ?? '').split('__HIGH__');
      const between = midParts[0] ?? '';
      const afterHigh = midParts[1] ?? '';
      priceEl.innerHTML = `${this.escapeHtmlForPriceRange(beforeLow)}<span class="product-row__price-current__amount">${lowStr}</span>${this.escapeHtmlForPriceRange(between)}<span class="product-row__price-current__amount">${highStr}</span>${this.escapeHtmlForPriceRange(afterHigh)}`;
      return;
    }

    if (tpl) {
      priceEl.classList.remove('product-row__price-current--range');
      priceEl.textContent = tpl.replace(/__LOW__/g, lowStr).replace(/__HIGH__/g, highStr);
      return;
    }

    priceEl.innerHTML = `<span class="product-row__price-current__amount">${lowStr}</span><span class="product-row__price-current__label"> – </span><span class="product-row__price-current__amount">${highStr}</span>`;
  }

  refreshRowTotal(productRow) {
    const commerceRoot = this.getProductRowCommerceRoot(productRow);
    const totalElement = commerceRoot?.querySelector('[data-total]');
    if (!totalElement) return;

    let sumCents = 0;
    let unitSum = 0;
    const detailsRoot = this.getProductRowDetailsRoot(productRow);
    const quantityScope = detailsRoot ?? productRow;
    quantityScope.querySelectorAll('.quantity-wrapper').forEach((wrap) => {
      const handler = wrap.querySelector('[data-wpd-price-handler]');
      const input = wrap.querySelector('[data-quantity]');
      if (!handler || !input) return;

      const unitCents = this.parseMoneyTextToCents(handler.textContent);
      const qty = parseInt(input.value, 10) || 0;
      unitSum += qty;
      if (!Number.isNaN(unitCents)) {
        sumCents += unitCents * qty;
      }
    });

    const unitsEl = commerceRoot?.querySelector('[data-subtotal-units]');
    if (unitsEl) {
      const tpl = this.dataset.subtotalUnitsTemplate;
      if (tpl) {
        unitsEl.textContent = tpl.split('__N__').join(String(unitSum));
      }
    }

    productRow.querySelectorAll('.product-row__cell--details .variant-qty-cell').forEach((cell) => {
      const input = cell.querySelector('[data-quantity]');
      const n = input ? parseInt(input.value, 10) || 0 : 0;
      cell.classList.toggle('variant-qty-cell--active', n > 0);
    });

    if (!Number.isFinite(sumCents)) {
      totalElement.textContent = this.formatMoney(0);
      return;
    }

    totalElement.textContent = this.formatMoney(sumCents);
  }

  refreshAllRowTotals() {
    this.querySelectorAll('.product-row').forEach((row) => {
      this.refreshProductRowPriceLabelFromHandlers(row);
      this.refreshProductRowCompareDisplay(row);
      this.refreshRowTotal(row);
      this.updateSubmitButtonState(row);
    });
  }

  getRowQuantitySnapshot(productRow) {
    const map = new Map();
    productRow.querySelectorAll('[data-quantity]').forEach((inp) => {
      const vid = inp.dataset.variantId;
      if (!vid) {
        return;
      }
      map.set(String(vid), Math.max(0, parseInt(inp.value, 10) || 0));
    });
    return map;
  }

  snapshotHasPositiveQty(snapshot) {
    for (const qty of snapshot.values()) {
      if (qty > 0) {
        return true;
      }
    }
    return false;
  }

  setOrderBaselineFromRow(productRow) {
    this._orderBaselines.set(productRow, this.getRowQuantitySnapshot(productRow));
  }

  isRowDirty(productRow) {
    const base = this._orderBaselines.get(productRow);
    if (!base) {
      return false;
    }
    const cur = this.getRowQuantitySnapshot(productRow);
    for (const [k, v] of cur) {
      if ((base.get(k) ?? 0) !== v) {
        return true;
      }
    }
    for (const [k, v] of base) {
      if ((cur.get(k) ?? 0) !== v) {
        return true;
      }
    }
    return false;
  }

  rowHasCommittedCartItems(productRow) {
    const base = this._orderBaselines.get(productRow);
    if (!base) {
      return false;
    }
    return this.snapshotHasPositiveQty(base);
  }

  updateSubmitButtonState(productRow) {
    const btn = this.getProductRowCommerceRoot(productRow)?.querySelector(
      '[data-submit-product-order]'
    );
    if (!btn) {
      return;
    }
    if (btn.dataset.submitting === 'true') {
      return;
    }
    const dirty = this.isRowDirty(productRow);
    const hasCommitted = this.rowHasCommittedCartItems(productRow);
    const cur = this.getRowQuantitySnapshot(productRow);
    const anyCurrent = this.snapshotHasPositiveQty(cur);

    let label = this.dataset.orderLabelAdd;
    if (hasCommitted && dirty) {
      label = this.dataset.orderLabelUpdate;
    } else if (hasCommitted && !dirty) {
      label = this.dataset.orderLabelIn;
    }
    btn.textContent = label;

    if (hasCommitted && !dirty) {
      btn.classList.add('the-grid__submit-order--synced');
    } else {
      btn.classList.remove('the-grid__submit-order--synced');
    }

    const disable =
      (hasCommitted && !dirty) || (!hasCommitted && !dirty && !anyCurrent);
    btn.disabled = disable;
  }

  setupPriceObservers() {
    const observer = new MutationObserver((mutations) => {
      const rows = new Set();
      mutations.forEach((m) => {
        const node = m.target;
        const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
        if (!el || !el.closest) return;
        const handler = el.closest('[data-wpd-price-handler]');
        if (!handler) return;
        const row = handler.closest('.product-row');
        if (row) rows.add(row);
      });
      rows.forEach((row) => {
        this.refreshProductRowPriceLabelFromHandlers(row);
        this.refreshProductRowCompareDisplay(row);
        this.refreshRowTotal(row);
      });
    });

    this.querySelectorAll('[data-wpd-price-handler]').forEach((el) => {
      observer.observe(el, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    });

    this._priceObserver = observer;
  }

  attachEventListeners() {
    this.tabButtons.forEach((button) => {
      button.addEventListener('click', () => this.handleTabClick(button));
    });

    this.querySelectorAll('.view-more-button').forEach((button) => {
      button.addEventListener('click', () => this.handleViewMore(button));
    });

    this.addEventListener('change', (e) => {
      const radio = e.target.closest?.(
        '.product-row__cell--details input[type="radio"][data-product-handle]'
      );
      if (!radio || !this.contains(radio)) {
        return;
      }
      if (radio.closest('.product-row__colors-group')) {
        return;
      }
      this.handleVariantChange(radio);
    });

    this.addEventListener('click', (e) => {
      const swatchBtn = e.target.closest('[data-the-grid-swatch]');
      if (swatchBtn && this.contains(swatchBtn)) {
        e.preventDefault();
        void this.handleSwatchProductChange(swatchBtn);
        return;
      }

      const submitBtn = e.target.closest('[data-submit-product-order]');
      if (submitBtn && this.contains(submitBtn)) {
        const productRow = submitBtn.closest('.product-row');
        if (productRow) {
          void this.submitProductOrder(productRow, submitBtn);
        }
        return;
      }

      const dec = e.target.closest('.quantity-btn.minus');
      const inc = e.target.closest('.quantity-btn.plus');
      if (!dec && !inc) return;
      const wrap = (dec || inc).closest('.quantity-wrapper');
      const input = wrap && wrap.querySelector('[data-quantity]');
      if (!input || !this.contains(input)) return;
      this.handleQuantityChange(input, inc ? 1 : -1);
    });

    this.addEventListener('change', (e) => {
      const input = e.target.closest && e.target.closest('[data-quantity]');
      if (!input || !this.contains(input)) return;
      this.handleQuantityChange(input);
    });

    this.addEventListener(
      'blur',
      (e) => {
        const input = e.target.closest && e.target.closest('[data-quantity]');
        if (!input || !this.contains(input) || input.disabled) return;
        this.handleQuantityChange(input);
      },
      true
    );
  }

  snapToCasePackQuantity(value, step, minimumQuantity) {
    if (!Number.isFinite(value) || value <= 0) {
      return 0;
    }
    if (step <= 1) {
      return value;
    }
    const packed = Math.ceil(value / step) * step;
    return Math.max(packed, minimumQuantity);
  }

  normalizeQuantityInputValue(input) {
    const parsed = parseInt(String(input.value).trim(), 10);
    const raw = Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
    const step = this.isWholesaleUser ? this.getQuantityStep(input) : 1;
    const minimumQuantity = this.isWholesaleUser
      ? (parseInt(input.dataset.minimumQuantity, 10) || 1)
      : 1;
    const normalized = this.snapToCasePackQuantity(raw, step, minimumQuantity);
    input.value = String(normalized);
    return normalized;
  }

  getQuantityStep(input) {
    const raw = input.dataset.quantityStep;
    if (raw === undefined || raw === '') {
      return 1;
    }
    const step = parseInt(raw, 10);
    return Number.isFinite(step) && step > 0 ? step : 1;
  }

  handleTabClick(button) {
    const collection = button.dataset.collection;

    this.tabButtons.forEach((btn) => btn.classList.remove('active'));
    button.classList.add('active');

    this.productRows.forEach((row) => {
      if (collection === 'all' || row.dataset.collection === collection) {
        row.style.display = '';
      } else {
        row.style.display = 'none';
      }
    });

    this.viewMoreRows.forEach((row) => {
      if (collection === 'all' || row.dataset.collection === collection) {
        row.style.display = '';
      } else {
        row.style.display = 'none';
      }
    });
  }

  async handleSwatchProductChange(swatchBtn) {
    const productRow = swatchBtn.closest('.product-row');
    const handle = swatchBtn.dataset.productHandle;
    if (!productRow || !handle) {
      return;
    }
    if (swatchBtn.classList.contains('button--swatch-active')) {
      return;
    }

    const collection = productRow.dataset.collection || '';
    const hidden = productRow.classList.contains('hidden-row') ? '1' : '0';
    const url = `/products/${encodeURIComponent(handle)}?view=the-grid-row&collection=${encodeURIComponent(collection)}&hidden=${hidden}`;

    productRow.classList.add('is-loading');

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load product: ${response.status}`);
      }

      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const newRow = doc.querySelector('.product-row');
      if (!newRow) {
        throw new Error('Product row markup not found');
      }

      productRow.replaceWith(newRow);
      newRow.classList.remove('is-loading');
      this.productRows = this.querySelectorAll('.product-row');
      this.setupPriceObservers();
      this.invokeReplaceWPDPriceHandlers();
      this.syncAllProductRowPriceLabelsAndTotals();
      await this.initializeCartQuantities();
    } catch (error) {
      productRow.classList.remove('is-loading');
      this.showNotification(`Error loading product: ${error.message}`, true);
    }
  }

  handleViewMore(button) {
    const collection = button.dataset.collection;
    const list = button.closest('.the-grid__list');
    if (!list) return;
    const hiddenRows = list.querySelectorAll(`.product-row[data-collection="${collection}"].hidden-row`);
    const viewMoreRow = button.closest('.view-more-row');

    hiddenRows.forEach((row) => {
      row.classList.remove('hidden-row');
    });

    viewMoreRow.style.display = 'none';
  }

  handleVariantChange(radio) {
    const productRow = radio.closest('.product-row');
    if (!productRow) {
      this.showNotification('Error: Product row not found', true);
      return;
    }

    const productId = radio.dataset.productId;
    if (!productId) {
      this.showNotification('Error: Product ID not found', true);
      return;
    }

    const detailsRoot = this.getProductRowDetailsRoot(productRow);
    if (!detailsRoot) {
      this.showNotification('Error: Product details not found', true);
      return;
    }

    const lastGroup = detailsRoot.querySelector('[data-last-option-group]');
    let maxOptionPos = 0;
    detailsRoot.querySelectorAll('.option-group').forEach((g) => {
      const p = parseInt(g.dataset.optionPosition, 10) || 0;
      if (p > maxOptionPos) maxOptionPos = p;
    });
    const pendingGroups = detailsRoot.querySelectorAll(
      '.option-group:not(.product-row__colors-group):not([data-last-option-group])'
    );
    for (const group of pendingGroups) {
      if (!group.querySelector('input[type="radio"]:checked')) {
        return;
      }
    }

    this.updateColorHeadingFromDom(productRow);

    void this.syncProductRowAfterOptionChange(productRow, productId);
  }

  handleQuantityChange(input, direction = 0) {
    const minimumQuantity = this.isWholesaleUser
      ? (parseInt(input.dataset.minimumQuantity, 10) || 1)
      : 1;
    const step = this.isWholesaleUser ? this.getQuantityStep(input) : 1;
    let currentValue = parseInt(input.value, 10) || 0;

    if (direction !== 0) {
      let newValue = currentValue;

      if (direction > 0) {
        if (currentValue === 0) {
          newValue = Math.max(minimumQuantity, step);
        } else {
          newValue = currentValue + step;
        }
      } else if (currentValue > 0) {
        if (currentValue < step || currentValue <= minimumQuantity) {
          newValue = 0;
        } else {
          newValue = currentValue - step;
          if (newValue > 0 && newValue < minimumQuantity) {
            newValue = 0;
          }
        }
      }

      if (newValue >= 0) {
        input.value = String(newValue);
        currentValue = newValue;
      }
    } else {
      currentValue = this.normalizeQuantityInputValue(input);
    }

    this.updateQuantity(input);
  }

  getSelectedOptionMap(productRow, detailsRoot) {
    const map = {};
    const colorGroup = detailsRoot.querySelector('.product-row__colors-group');

    if (colorGroup) {
      const pos = parseInt(colorGroup.dataset.optionPosition, 10);
      const activeSwatch = productRow.querySelector(
        '[data-the-grid-swatch].button--swatch-active'
      );
      let colorVal =
        activeSwatch?.getAttribute('title') ||
        activeSwatch?.querySelector('.visually-hidden')?.textContent?.trim();

      if (!colorVal) {
        const checked = colorGroup.querySelector('input[type="radio"]:checked');
        colorVal = checked?.value;
      }

      if (colorVal) {
        map[pos] = colorVal;
      }
    }

    detailsRoot
      .querySelectorAll(
        '.option-group:not(.product-row__colors-group):not([data-last-option-group])'
      )
      .forEach((group) => {
        const pos = parseInt(group.dataset.optionPosition, 10);
        const checked = group.querySelector('input[type="radio"]:checked');
        if (checked) {
          map[pos] = checked.value;
        }
      });

    return map;
  }

  findVariantForGridRow(product, optionMap, lastOptionPosition, lastValue) {
    return product.variants.find((variant) => {
      if (variant[`option${lastOptionPosition}`] !== lastValue) {
        return false;
      }

      for (const [pos, val] of Object.entries(optionMap)) {
        if (variant[`option${pos}`] !== val) {
          return false;
        }
      }

      return true;
    });
  }

  async syncProductRowAfterOptionChange(productRow, productId) {
    try {
      const detailsRoot = this.getProductRowDetailsRoot(productRow);
      if (!detailsRoot) {
        return;
      }

      const productHandle = productRow.dataset.productHandle;
      if (!productHandle) {
        return;
      }

      const productUrl = `/products/${productHandle}.js`;
      const response = await fetch(productUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const product = await response.json();

      const lastGroup = detailsRoot.querySelector('[data-last-option-group]');
      if (!lastGroup) {
        return;
      }

      const optionMap = this.getSelectedOptionMap(productRow, detailsRoot);
      const lastOptionPosition = parseInt(lastGroup.dataset.optionPosition, 10);

      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error('Failed to fetch cart');
      }
      const cart = await cartResponse.json();

      lastGroup.querySelectorAll('.variant-qty-cell').forEach((cell) => {
        const lastValue = cell.getAttribute('data-option-value') ?? '';
        const variant = this.findVariantForGridRow(
          product,
          optionMap,
          lastOptionPosition,
          lastValue
        );
        if (!variant) {
          return;
        }

        const quantityInput = cell.querySelector('[data-quantity]');
        const quantityWrapper = cell.querySelector('.quantity-wrapper');
        if (!quantityInput || !quantityWrapper) {
          return;
        }

        const handler = quantityWrapper.querySelector('[data-wpd-price-handler]');
        if (handler) {
          handler.setAttribute('data-wpd-variant-id', String(variant.id));
          handler.setAttribute('data-wpd-variant-price', String(variant.price));
          if (variant.compare_at_price) {
            handler.setAttribute('data-wpd-variant-compare-at-price', String(variant.compare_at_price));
          } else {
            handler.removeAttribute('data-wpd-variant-compare-at-price');
          }
          handler.textContent = this.formatMoney(variant.price);
        }

        quantityInput.dataset.variantId = String(variant.id);
        if (this.isWholesaleUser) {
          quantityInput.dataset.minimumQuantity = String(
            this.getVariantMinimumQuantity(productRow, variant.id)
          );
          quantityInput.dataset.quantityStep = String(
            this.getVariantCasePackQuantity(productRow, variant.id)
          );
        }

        const cartItem = cart.items.find(
          (item) => item.variant_id.toString() === String(variant.id)
        );
        quantityInput.value = cartItem ? String(cartItem.quantity) : '0';

        if (variant.available) {
          cell.classList.remove('variant-qty-cell--unavailable');
          quantityWrapper.classList.remove('disabled');
          quantityInput.disabled = false;
        } else {
          cell.classList.add('variant-qty-cell--unavailable');
          quantityWrapper.classList.add('disabled');
          quantityInput.disabled = true;
        }
      });

      this.updateAvailabilityStates(productRow, product);
      this.invokeReplaceWPDPriceHandlers();
      this.refreshProductRowPriceLabelFromHandlers(productRow);
      this.refreshProductRowCompareDisplay(productRow);
      this.refreshRowTotal(productRow);
      this.updateColorHeadingFromDom(productRow);
      this.setOrderBaselineFromRow(productRow);
      this.updateSubmitButtonState(productRow);
    } catch (error) {
      this.showNotification(`Error updating variant: ${error.message}`, true);
    }
  }

  updateAvailabilityStates(productRow, product) {
    const detailsRoot = this.getProductRowDetailsRoot(productRow);
    if (!detailsRoot) {
      return;
    }

    detailsRoot.querySelectorAll('input[type="radio"][data-product-handle]').forEach((input) => {
      const label = input.nextElementSibling;
      if (label?.tagName !== 'LABEL') {
        return;
      }
      label.classList.remove('disabled');
      input.disabled = false;
    });

    const lastGroup = detailsRoot.querySelector('[data-last-option-group]');
    if (lastGroup) {
      const optionMap = this.getSelectedOptionMap(productRow, detailsRoot);
      const lastOptionPosition = parseInt(lastGroup.dataset.optionPosition, 10);

      lastGroup.querySelectorAll('.variant-qty-cell').forEach((cell) => {
        const lastValue = cell.getAttribute('data-option-value') ?? '';
        const variant = this.findVariantForGridRow(
          product,
          optionMap,
          lastOptionPosition,
          lastValue
        );
        const quantityInput = cell.querySelector('[data-quantity]');
        const quantityWrapper = cell.querySelector('.quantity-wrapper');
        if (!variant || !quantityInput || !quantityWrapper) {
          return;
        }
        if (variant.available) {
          cell.classList.remove('variant-qty-cell--unavailable');
          quantityWrapper.classList.remove('disabled');
          quantityInput.disabled = false;
        } else {
          cell.classList.add('variant-qty-cell--unavailable');
          quantityWrapper.classList.add('disabled');
          quantityInput.disabled = true;
        }
      });
    }

    product.variants.forEach((variant) => {
      if (!variant.available) {
        const optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        optionValues.forEach((value) => {
          const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value.replace(/"/g, '\\"');
          detailsRoot.querySelectorAll(`input[type="radio"][value="${escaped}"]`).forEach((inp) => {
            const label = inp.nextElementSibling;
            if (label?.tagName !== 'LABEL') {
              return;
            }
            label.classList.add('disabled');
            inp.disabled = true;
          });
        });
      }
    });
  }

  updateQuantity(input) {
    const quantity = parseInt(input.value, 10);
    const minimumQuantity = this.isWholesaleUser
      ? (parseInt(input.dataset.minimumQuantity, 10) || 1)
      : 1;
    const productRow = input.closest('.product-row');

    if (this.isWholesaleUser && quantity > 0 && quantity < minimumQuantity) {
      this.showNotification(`Minimum quantity is ${minimumQuantity}, you have ${quantity}`, true);
      return;
    }

    this.refreshRowTotal(productRow);
    this.updateSubmitButtonState(productRow);
  }

  async submitProductOrder(productRow, button) {
    const productId = productRow.dataset.productId;
    if (!productId) {
      return;
    }

    const inputs = [...productRow.querySelectorAll('[data-quantity]')].filter(
      (inp) => inp.dataset.variantId && !inp.disabled
    );

    for (const input of inputs) {
      const qty = parseInt(input.value, 10) || 0;
      if (qty <= 0) {
        continue;
      }
      const minimumQuantity = this.isWholesaleUser
        ? (parseInt(input.dataset.minimumQuantity, 10) || 1)
        : 1;
      if (this.isWholesaleUser && qty < minimumQuantity) {
        this.showNotification(`Minimum quantity is ${minimumQuantity}, you have ${qty}`, true);
        return;
      }
    }

    button.dataset.submitting = 'true';
    button.disabled = true;

    try {
      let cart = await fetch('/cart.js').then((r) => {
        if (!r.ok) {
          throw new Error('Failed to fetch cart');
        }
        return r.json();
      });

      let didMutate = false;

      for (const input of inputs) {
        const variantId = input.dataset.variantId;
        const want = Math.max(0, parseInt(input.value, 10) || 0);
        const item = cart.items.find((line) => String(line.variant_id) === String(variantId));
        const have = item ? item.quantity : 0;
        if (want === have) {
          continue;
        }

        didMutate = true;

        if (item && want === 0) {
          await this.removeFromCart(item.key, true);
        } else if (item && want > 0) {
          await this.updateCartItemQuantity(item.key, want, true);
        } else if (!item && want > 0) {
          await this.addToCart(variantId, want, true);
        }

        cart = await fetch('/cart.js').then((r) => {
          if (!r.ok) {
            throw new Error('Failed to fetch cart');
          }
          return r.json();
        });
      }

      if (didMutate) {
        this.setOrderBaselineFromRow(productRow);
        this.updateSubmitButtonState(productRow);
        await this.updateCartSections();
        this.invokeReplaceWPDPriceHandlers();
        this.refreshProductRowPriceLabelFromHandlers(productRow);
        this.refreshProductRowCompareDisplay(productRow);
        this.refreshRowTotal(productRow);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'the-grid',
            cartData: cart,
          });
        } else {
          this.applyCartQuantities(cart);
        }

        if (typeof updateCartCounters === 'function') {
          updateCartCounters();
        }

        const msg = this.dataset.orderUpdatedMessage;
        if (msg) {
          this.showNotification(msg);
        }
      } else {
        this.updateSubmitButtonState(productRow);
      }
    } catch (error) {
      this.showNotification(`Error updating cart: ${error.message}`, true);
    } finally {
      button.dataset.submitting = 'false';
      this.updateSubmitButtonState(productRow);
    }
  }

  getVariantMinimumQuantity(productRow, variantId) {
    const minimumQuantityInput = productRow.querySelector(
      `[data-variant-minimum-quantity][data-variant-id="${variantId}"]`
    );

    if (!minimumQuantityInput) {
      return 1;
    }

    return parseInt(minimumQuantityInput.value, 10) || 1;
  }

  getVariantCasePackQuantity(productRow, variantId) {
    const casePackInput = productRow.querySelector(
      `[data-variant-case-pack-quantity][data-variant-id="${variantId}"]`
    );

    if (!casePackInput) {
      return 1;
    }

    return parseInt(casePackInput.value, 10) || 1;
  }

  async updateCartSections() {
    try {
      const sections = ['cart-drawer', 'cart-counter'];
      const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
      if (!sectionsResponse.ok) throw new Error('Failed to fetch sections');

      const sectionsData = await sectionsResponse.json();

      document.querySelectorAll('#CartDrawer-Body').forEach((cartDrawerBody) => {
        if (sectionsData['cart-drawer']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionsData['cart-drawer'], 'text/html');
          const newContent = doc.querySelector('#CartDrawer-Body');
          if (newContent) {
            cartDrawerBody.innerHTML = newContent.innerHTML;
          }
        }
      });

      document.querySelectorAll('#shopify-section-cart-counter').forEach((cartCounter) => {
        if (sectionsData['cart-counter']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionsData['cart-counter'], 'text/html');
          const newContent = doc.querySelector('#shopify-section-cart-counter');
          if (newContent) {
            cartCounter.innerHTML = newContent.innerHTML;
          }
        }
      });
    } catch (error) {
      console.error('Error updating cart sections:', error);
    }
  }

  async updateCartItemQuantity(itemKey, quantity, silent = false) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: quantity,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.description || `Failed to update cart: ${response.status}`);
      }

      await response.json();
      if (!silent) {
        this.showNotification('Cart updated');
      }
    } catch (error) {
      if (!silent) {
        this.showNotification(`Error updating cart item: ${error.message}`, true);
      }
      throw error;
    }
  }

  async removeFromCart(itemKey, silent = false) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.description || `Failed to remove item: ${response.status}`);
      }

      await response.json();
      if (!silent) {
        this.showNotification('Item removed from cart');
      }
    } catch (error) {
      if (!silent) {
        this.showNotification(`Error removing item from cart: ${error.message}`, true);
      }
      throw error;
    }
  }

  async addToCart(variantId, quantity, silent = false) {
    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: quantity,
        }],
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.description || `Failed to add item: ${response.status}`);
      }

      await response.json();
      if (!silent) {
        this.showNotification('Item added to cart');
      }
    } catch (error) {
      if (!silent) {
        this.showNotification(`Error adding to cart: ${error.message}`, true);
      }
      throw error;
    }
  }

  showNotification(message, isError = false) {
    this.notification.textContent = message;
    this.notification.className = `notification ${isError ? 'error' : ''}`;
    this.notification.classList.add('show');

    setTimeout(() => {
      this.notification.classList.remove('show');
    }, 3000);
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') {
      this.showNotification('Error: Invalid price value', true);
      return '$0.00';
    }
    return '$' + (cents / 100).toFixed(2);
  }

  applyCartQuantities(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return;
    }

    this.productRows = this.querySelectorAll('.product-row');

    for (const row of this.productRows) {
      if (!row.classList || !row.classList.contains('product-row')) {
        continue;
      }

      row.querySelectorAll('[data-quantity]').forEach((quantityInput) => {
        const variantId = quantityInput.dataset.variantId;
        if (!variantId) {
          return;
        }
        const cartItem = cart.items.find(
          (item) => String(item.variant_id) === String(variantId)
        );
        quantityInput.value = cartItem ? String(cartItem.quantity) : '0';
      });

      this.refreshProductRowPriceLabelFromHandlers(row);
      this.refreshProductRowCompareDisplay(row);
      this.refreshRowTotal(row);
      this.updateColorHeadingFromDom(row);
      this.setOrderBaselineFromRow(row);
      this.updateSubmitButtonState(row);
    }

    this.invokeReplaceWPDPriceHandlers();
    this.refreshAllRowTotals();
  }

  async initializeCartQuantities() {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error('Failed to fetch cart');
      }
      const cart = await cartResponse.json();
      this.applyCartQuantities(cart);
      requestAnimationFrame(() => this.refreshAllRowTotals());
      setTimeout(() => this.refreshAllRowTotals(), 0);
      setTimeout(() => this.refreshAllRowTotals(), 100);
    } catch (error) {
      console.error('Error initializing cart quantities:', error);
      this.querySelectorAll('.product-row').forEach((row) => {
        this.setOrderBaselineFromRow(row);
        this.updateSubmitButtonState(row);
      });
    }
  }

  static installCartSyncHooks() {
    if (window.__theGridCartHooksInstalled) {
      return;
    }
    window.__theGridCartHooksInstalled = true;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
        const cartData = payload && payload.cartData;
        document.querySelectorAll('the-grid').forEach((grid) => {
          if (cartData && Array.isArray(cartData.items)) {
            grid.applyCartQuantities(cartData);
          } else {
            void grid.initializeCartQuantities();
          }
        });
      });
    }

    if (typeof updateCartCounters === 'function') {
      const originalUpdateCartCounters = updateCartCounters;
      window.updateCartCounters = async function (...args) {
        await originalUpdateCartCounters.apply(this, args);
        document.querySelectorAll('the-grid').forEach((grid) => {
          void grid.initializeCartQuantities();
        });
      };
    }
  }
}

if (!customElements.get('the-grid')) {
  customElements.define('the-grid', TheGrid);
}

TheGrid.installCartSyncHooks();
